import {
  CircularLoader,
  ErrorPageLayout,
  LoadingPageLayout,
} from '../../presentation';
import { ReactNode, useState } from 'react';
import {
  StatusDagIdEnum,
  StatusTypeEnum,
  TargetAreaType,
  TargetType,
  statusValueToStatusInfo,
  useShouldPoll,
  useStatusesQuery,
  useStatusesSubscription,
} from '@converge/graphql';
import { useEffect, useMemo } from 'react';

import { ISubmissionProps } from './overview';
import { displayFormatDate } from '@converge/utils';
import { useOutletContext } from 'react-router-dom';
import { useUnderwritingSubmissionDetailPage } from './underwriting.submission.detail.hooks';

export interface IBlockUntilScoreAndRateFinishes {
  children: ReactNode;
  skipCheck?: boolean;
  isRiskProfile?: boolean;
}

export const BlockUntilScoreAndRateFinishes = ({
  children,
  skipCheck,
  isRiskProfile,
}: IBlockUntilScoreAndRateFinishes) => {
  const { updateStatus } = useStatusesSubscription();
  const {
    params: { submissionUuid },
  } = useUnderwritingSubmissionDetailPage();
  const { isScoringDone } = useOutletContext<ISubmissionProps>();
  const [enableRiskProfile, setEnableRiskProfile] = useState(false);
  const [isScoreAndRateDone, setIsScoreAndRateDone] = useState(false);

  const displayRiskProfile = useMemo(() => {
    return (
      enableRiskProfile ||
      isScoringDone ||
      updateStatus?.noticeValue === 'completed'
    );
  }, [enableRiskProfile, isScoringDone, updateStatus]);

  useEffect(() => {
    if (updateStatus) {
      const { noticeDagId, noticeValue, noticeTask, noticeArea } = updateStatus;

      if (
        noticeDagId === 'SCORE_AND_RATE' &&
        noticeValue === 'completed' &&
        noticeTask === 'root' &&
        noticeArea === 'Score & Rate'
      ) {
        setIsScoreAndRateDone(true);
      }
    }
  }, [updateStatus]);
  const { poll, setPoll } = useShouldPoll();

  const {
    info: { statuses },
    loading: statusesLoading,
  } = useStatusesQuery({
    operationName: 'riskProfile',
    poll,
    skip: skipCheck,
    filters: {
      targetUuid: submissionUuid,
      targetArea: TargetAreaType.Submission,
      targetName: 'status',
      details: false,
      statusType: StatusTypeEnum.Dag,
      statusDagId: StatusDagIdEnum.ScoreAndRate,
      targetType: TargetType.Node,
    },
  });

  const {
    info: { statuses: scoringStatus },
  } = useStatusesQuery({
    operationName: 'riskProfile',
    poll: true,
    filters: {
      targetUuid: submissionUuid,
      targetArea: TargetAreaType.Submission,
      targetName: 'status',
      details: false,
      statusType: StatusTypeEnum.Processor,
      statusDagId: StatusDagIdEnum.ScoreAndRate,
      statusArea: 'Scoring',
    },
  });

  useEffect(() => {
    const status = (scoringStatus ?? [])[0];
    if (status?.statusValue === 'completed') {
      setEnableRiskProfile(true);
    }
  }, [scoringStatus]);

  const { scoringAndRating, lastUpdatedAtText } = useMemo(() => {
    const status = (statuses ?? [])[0];
    const statusInfo = statusValueToStatusInfo(status?.statusValue);

    if (statusInfo.finished) {
      if (poll === true) {
        setPoll(false);
      }
    } else {
      if (displayRiskProfile && isRiskProfile) {
        setPoll(false);
      } else {
        setPoll(true);
      }
    }

    return {
      scoringAndRating: statusInfo,
      lastUpdatedAtText: status
        ? `${
            status.updatedAt === status.createdAt ? 'Updated' : 'First updated'
          } at ${displayFormatDate(
            status.createdAt,
            displayFormatDate.DateFormatType.USADateFormatWithTimeAndSecondsAt
          )}.${
            status.updatedAt !== status.createdAt
              ? ` Last update at ${displayFormatDate(
                  status.updatedAt,
                  displayFormatDate.DateFormatType
                    .USADateFormatWithTimeAndSecondsAt
                )}.`
              : ''
          }`
        : `No updates yet`,
    };
  }, [poll, setPoll, statuses, displayRiskProfile, isRiskProfile]);

  if (scoringAndRating.failed || updateStatus?.noticeValue === 'cancelled') {
    return (
      <ErrorPageLayout
        message={'Scoring & rating failed'}
        messageId="underwriting.scoringAndRating.error.message"
        subtitle="Please contact support for information."
        subtitleId="underwriting.scoringAndRating.error.subtitle"
        subtitleTooltip={lastUpdatedAtText}
      />
    );
  }

  if (
    (scoringAndRating.loading && !displayRiskProfile && isRiskProfile) ||
    (scoringAndRating.loading && !isScoreAndRateDone && !isRiskProfile)
  ) {
    return (
      <LoadingPageLayout
        title="Scoring & rating submission..."
        titleId="underwriting.scoringAndRating.loading.title"
        subtitle={
          poll ? 'Please wait a moment' : 'Please refresh the page periodically'
        }
        subtitleId="underwriting.scoringAndRating.loading.subtitle"
        subtitleTooltip={lastUpdatedAtText}
        loading={statusesLoading}
      />
    );
  }

  if (scoringAndRating.pending) {
    return (
      <LoadingPageLayout
        title="Scoring and rating is pending..."
        titleId="underwriting.scoringAndRating.pending.title"
        subtitle={
          poll ? 'Please wait a moment' : 'Please refresh the page periodically'
        }
        subtitleId="underwriting.scoringAndRating.pending.subtitle"
        subtitleTooltip={lastUpdatedAtText}
        loading={statusesLoading}
      />
    );
  }

  if (statusesLoading) {
    return <CircularLoader className="circular-loader-style" />;
  }

  return <>{children}</>;
};
